// setup.ts — WebSocket lifecycle: connect, channel-setup on open, parse on
// message, reconnect on close. Owns the module-shared `ws` / `sl_context`
// state binding (imported from state.ts) and the `Object.defineProperty`
// getter that lets external bare-identifier readers (including template.js's
// debug log) observe `ws.readyState` and stay
// in sync with reconnect re-assignments.

import { ws, sl_context, setWs, setSlContext } from './state';
import {
    subscribeToCaster,
    subscribeToEncounterRollChannel,
    safeSubscribe,
    safeUnsubscribe,
    reconcileCasterChannelForEncounterLoad,
} from './channels';
import { processWebSocketMessage, handleWebSocketMessage } from './router';
import { mountPlayerEncounterCatchup } from './player-encounter-transfer';
import { fetchAndCacheCaster } from './caster-cache';
import { encounterReplay } from './encounter-replay';
import { resyncCaster } from './caster-delta';
import { markFt3dEventDelayDiagnostic } from '../shared/ft3d-event-delay-diagnostic';
import { refreshFt3dCurtains } from './ft3d-curtain-refresh';
import { mountMagicEffects, consumeMagicTransportControl, handleMagicEffect } from './ft3d-magic-effect';
import type { MagicEffect } from '../shared/magic-effect-contract';
import { claimGameTab, gameTabIdle } from './single-tab';
import { consumeAoePreviewTransportControl } from './ft3d-aoe-placement';
import { consumeTargetingPreviewTransportControl } from './ft3d-ability-targeting';
import { ftCurrentTruthSink } from './truth-sink';
import { consumeTruthTransportControl, truthSocketClosed } from './truth-transport';

// Each site authenticates against its own host-only session cookie.
function websocketUrl(): string {
    return location.hostname === 'staging.fatetide.com'
        ? 'wss://staging.fatetide.com/ws/transport'
        : 'wss://fatetide.com:8080';
}

// Encounter cursors survive reconnect; unnumbered channels keep their existing recovery.
let openedOnce = false;
let resyncOnSubscribe = false;

function setupWebSocketHandlers(socket: WebSocket): void {
    let recovering = false;
    const recoveredChannels = new Set<string>();

    socket.onopen = () => {
        if (ftCurrentTruthSink()) { socket.close(1000, 'local truth sink'); return; }
        recovering = openedOnce;

        const channels: string[] = [];

        // Pivot off hidden inputs — use the JS state injected by template_page_footer.php
        // (window.FateTide.user.{id,isGM} and window.FateTide.campaign.id) instead of
        // legacy hidden campaign/user inputs. window.FateTide.* is set
        // directly from PHP-side $user_id / $campaign_id and is the single source of truth.
        const _ft_user_id      = window.FateTide?.user?.id || '';
        const _ft_campaign_id  = window.FateTide?.campaign?.id || '';
        const _ft_encounter_id = String(window.ftGetActiveEncounterId?.() || sl_context?.encounter_id || '').trim();

        const _ft_is_gm = !!(window.FateTide && window.FateTide.user && window.FateTide.user.isGM);

        if (checkIfValidUUID(_ft_campaign_id)) {
            channels.push(`campaign:${_ft_campaign_id}`);

            // GM roll channel stays campaign-scoped — GMs running parallel encounters need
            // cross-encounter visibility, and the server always publishes :gm.
            if (_ft_is_gm) {
                channels.push(`roll:${_ft_campaign_id}:gm`);
            } else {
                // Players ALSO get the campaign-wide :player channel for GM-as-themselves
                // rolls (gm_pushed_campaign_time, narration events) that affect every
                // player regardless of which encounter they're in. This sub is static —
                // it doesn't rotate on encounter change. See insert_rolls_inc.php for
                // the campaign-vs-encounter routing discriminator.
                channels.push(`roll:${_ft_campaign_id}:player`);
            }
        }

        // Identity-targeted membership transitions (GM pulling/pushing this viewer's PC between
        // encounters). Unlike caster:{id}, this remains stable even when the plain campaign surface
        // has no selected action caster and the parent keeps no full encounter roster.
        if (checkIfValidUUID(_ft_user_id)) {
            channels.push(`user:${_ft_user_id}`);
        }

        if (checkIfValidUUID(_ft_encounter_id)) {
            channels.push(`encounter:${_ft_encounter_id}`);

            // Encounter-scope player roll channel: rolls anchored to a specific encounter
            // creature (PCs, NPCs). Rotates via subscribeToEncounterRollChannel() when the
            // player moves encounters so IC isolation holds.
            if (!_ft_is_gm) {
                channels.push(`roll:${_ft_encounter_id}:player`);
                window.FateTide = window.FateTide || {};
                window.FateTide.prevRollEncounter = _ft_encounter_id;
            }
        }
        subscribeToCaster();

        // User-scope roll channel: random-table / quick-roller without a campaign loaded.
        if (_ft_user_id) {
            channels.push(`roll:${_ft_user_id}`);
        }

        // Subscribe once so bridges that count subscriptions do not deliver duplicate frames.
        const uniqueChannels = Array.from(new Set(channels));
        uniqueChannels.forEach((ch) => {
            safeSubscribe(ch, socket);
        });
        refreshFt3dCurtains();
        // A reconnect missed every roll_insert in the gap; repaint the log from the server (on the
        // first subscription ack) so a result never waits for a manual reload. Actor stores also
        // recover on their OWN subscription ack below: a missed final delta has no later version
        // gap to detect, so waiting for the next gameplay action can leave bars stale indefinitely.
        if (openedOnce) {
            resyncOnSubscribe = true;
            // Stores kept current by pushes missed whatever the gap carried; listeners resync themselves
            // (teleport offers). The encounter mini-map has no read to resync with — it catches up on the
            // next push or page load.
            try { window.dispatchEvent(new CustomEvent('ft:ws-resubscribed')); } catch (_) { /* optional */ }
        }
        openedOnce = true;
        ftDebugLog('ws', { openSubscriptions: uniqueChannels });
    };

    socket.onmessage = async (event: MessageEvent) => {
        const raw = event.data;
        markFt3dEventDelayDiagnostic('parent.websocket.message-entry', raw);
        if (typeof raw === 'string') {
            try {
                const control = JSON.parse(raw) as { type?: string };
                if (encounterReplay.control(control)) return;
                if (consumeAoePreviewTransportControl(socket, control)) return;
                if (consumeTargetingPreviewTransportControl(socket, control)) return;
                const magicControl = consumeMagicTransportControl(control);
                const truthControl = consumeTruthTransportControl(socket, control);
                if (magicControl || truthControl) return;
                if (control.type === 'transport_subscription') {
                    // Repaint only once the reconnected socket is subscribed again, so no row can
                    // land between the server read and the live stream resuming.
                    const ack = control as { status?: string; channel?: string; replay?: boolean };
                    // Per-channel subscription acks for stores that must seed only once their push channel is LIVE
                    // (a read taken before the ack can miss a push published in between — teleport offers).
                    try { window.dispatchEvent(new CustomEvent('ft:ws-channel-subscribed', { detail: { socket, channel: String(ack.channel ?? ''), status: String(ack.status ?? ''), recovering } })); } catch (_) { /* optional */ }
                    const channel = String(ack.channel ?? '');
                    if (recovering && ack.status === 'subscribed' && !recoveredChannels.has(channel)) {
                        recoveredChannels.add(channel);
                        const [kind, id = ''] = channel.split(':');
                        if (checkIfValidUUID(id)) {
                            if (kind === 'encounter' && !ack.replay && Array.isArray(window.FateTide?.encounterCache?.get(id)?.encounter_creatures_arr)) {
                                void window.ftResyncEncounter?.(id);
                            } else if (kind === 'caster' && window.FateTide?.casterCache?.has(id)) {
                                void resyncCaster(id);
                            }
                        }
                    }
                    if (resyncOnSubscribe && ack.status === 'subscribed' && String(ack.channel ?? '').startsWith('roll:')) {
                        resyncOnSubscribe = false;
                        // Surfaces that mirror the log without owning it (the FT3D HUD log) reconcile the
                        // rows the gap swallowed once the repaint has landed.
                        if (typeof buildLog === 'function') {
                            Promise.resolve(buildLog()).then(() => {
                                try { window.dispatchEvent(new CustomEvent('ft:log-resynced')); } catch (_) { /* optional */ }
                            }, (error: unknown) => { ftDebugLog('ws', { logResyncFailed: String(error) }); });
                        }
                    }
                    return;
                }
                if (control.type === 'ft3d_magic_effect') { handleMagicEffect(control as MagicEffect); return; }
            } catch { /* Existing non-JSON bridge greeting is handled below. */ }
        }
        markFt3dEventDelayDiagnostic('parent.websocket.raw-callback', raw);

        if (raw instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
                processWebSocketMessage(reader.result);
            };
            reader.readAsText(raw);
        } else {
            processWebSocketMessage(raw);
        }
    };

    socket.onclose = (event: CloseEvent) => {
        truthSocketClosed(socket);
        encounterReplay.closed();
        ftDebugLog('websocket close', { code: event.code, reason: event.reason });

        // A newer game tab took over (single-tab.ts): this tab stays disconnected until the viewer picks "Use here".
        if (gameTabIdle() || ftCurrentTruthSink()) return;

        // Attempt to reconnect
        setTimeout(() => {
            if (gameTabIdle() || ftCurrentTruthSink()) return;
            ftDebugLog('websocket reconnect', {});
            const next = new WebSocket(websocketUrl());
            setWs(next);
            setupWebSocketHandlers(next);
        }, 5000);
    };

    socket.onerror = (error: Event) => {
        console.error('WebSocket Error:', error);
    };
}

// `ws` is re-assigned inside setupWebSocketHandlers' reconnect path (via setWs).
// A live getter keeps window.ws in sync without manual sync at each reassign.
Object.defineProperty(window, 'ws', { configurable: true, get: () => ws });

function startWebSocket(): void {
    if (ftCurrentTruthSink()) return;
    // The encounter wrapper owns chrome/audio/mode; its one scene frame owns the encounter
    // subscription and compile. A second socket here would reconcile the same scene twice.
    if (document.querySelector('[data-ft-encounter-surface]')) return;
    // Public pages share this bundle, but the transport requires a PHP session.
    // The server remains authoritative; avoid an anonymous handshake on login.
    if (!checkIfValidUUID(window.FateTide?.user?.id || '')) return;
    mountMagicEffects();
    mountPlayerEncounterCatchup();
    setSlContext(getSidelogContext());
    const next = new WebSocket(websocketUrl());
    setWs(next);
    setupWebSocketHandlers(next);
    // One live GAME tab per browser, newest wins (owner 2026-09-30): an older campaign tab of this user goes idle.
    claimGameTab(sl_context, () => { if (ws && ws.readyState <= WebSocket.OPEN) ws.close(4000, 'superseded by a newer tab'); });
}

window.addEventListener('beforeunload', () => {
    // This bundle registers before template.js arrives. Navigation can unload the
    // page in that gap, so cleanup must not call its late context helpers. Use the
    // channel we actually subscribed to, which may differ from the current caster.
    const casterChannelSub = window.FateTide?.casterChannelSub;
    if (casterChannelSub) safeUnsubscribe(`caster:${casterChannelSub}`);
    if (ws?.readyState === WebSocket.OPEN) ws.close();
});

window.startWebSocket = startWebSocket;
window.setupWebSocketHandlers = setupWebSocketHandlers;

// Namespace surface — preferred call form for new code.
window.FTWebSocket = {
    startWebSocket,
    setupWebSocketHandlers,
    safeSubscribe,
    safeUnsubscribe,
    subscribeToCaster,
    subscribeToEncounterRollChannel,
    reconcileCasterChannelForEncounterLoad,
    fetchAndCacheCaster,
    processWebSocketMessage,
    handleWebSocketMessage,
};

export { startWebSocket, setupWebSocketHandlers };
